/**
 * Card for an assessment.
 */
import * as React from 'react';
import { Building2, UserCheck, ClipboardList } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { getRowPermission } from '@/hooks/usePageActions';
import { useAuthStore } from '@/stores/authStore';
import { ASSESSMENT_STATUS_CONFIG, ASSESSMENT_TYPE_DEFINITIONS } from './assessment-configs';
import {
  deriveAssessmentSubjectName,
  formatAssessmentDate,
  resolveAssessmentRowActions,
} from './assessment-table-utils';
import type { Assessment } from '@/types/assessment.types';

/** Props for {@link AssessmentCard}. */
interface AssessmentCardProps {
  assessment: Assessment;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onValidate?: () => void;
  onExport?: () => void;
  canValidate?: boolean;
  canExport?: boolean;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean; canViewRow?: boolean };
}

/** Card for an assessment. */
export const AssessmentCard: React.FC<AssessmentCardProps> = ({
  assessment,
  onView,
  onEdit,
  onDelete,
  onValidate,
  onExport,
  canValidate = false,
  canExport = false,
  pageActions,
}) => {
  const roleCodes = useAuthStore((s) => s.roles).map((r) => r.code);
  const actions = resolveAssessmentRowActions(
    assessment,
    {
      edit: getRowPermission(assessment, 'action-edit-row', pageActions?.canEditRow ?? true),
      validate: canValidate,
      export: canExport,
      delete: getRowPermission(assessment, 'action-delete-row', pageActions?.canDeleteRow ?? true),
    },
    roleCodes,
  );

  const statusConfig = ASSESSMENT_STATUS_CONFIG[assessment.status];
  const StatusIcon = statusConfig.icon;
  const def = ASSESSMENT_TYPE_DEFINITIONS[assessment.type];

  const subjectName = deriveAssessmentSubjectName(assessment);

  return (
    <TableCard
      onClick={() => onView?.()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView?.();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="data-card-header" />

      <div className="data-card-content">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <ClipboardList className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex items-center flex-wrap" style={{ gap: '0.5rem' }}>
              <span className="font-medium text-foreground text-sm">{def.label}</span>
              <LabelBadge
                label={statusConfig.label}
                icon={StatusIcon}
                variant={statusConfig.badgeVariant}
                color={statusConfig.badgeColor}
                size="sm"
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {formatAssessmentDate(assessment.generatedAt)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <UserCheck className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="text-sm text-foreground">{subjectName}</span>
        </div>

        {assessment.establishment && (
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            <span className="text-sm text-foreground">{assessment.establishment.name}</span>
          </div>
        )}
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.();
            }}
          />
          {(pageActions?.canEditRow ?? true) && (
            <Button
              preset="row-edit"
              disabled={actions.edit.disabled}
              tooltipText={actions.edit.tooltip}
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
            />
          )}
          {canValidate && (
            <Button
              preset="row-validate"
              disabled={actions.validate.disabled}
              tooltipText={actions.validate.tooltip}
              onClick={(e) => {
                e.stopPropagation();
                onValidate?.();
              }}
            />
          )}
          {canExport && (
            <Button
              preset="row-download"
              disabled={actions.export.disabled}
              tooltipText={actions.export.tooltip}
              onClick={(e) => {
                e.stopPropagation();
                onExport?.();
              }}
            />
          )}
          {(pageActions?.canDeleteRow ?? true) && (
            <Button
              preset="row-delete"
              disabled={actions.delete.disabled}
              tooltipText={actions.delete.tooltip}
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
